import { Button } from "@/components/ui/button.tsx";
import { cn } from "@/lib/utils.ts";
import { Paperclip, X } from "lucide-react";

interface FileChipProps {
  /** Primary line — the file name. */
  name: string;
  /** Secondary line — a size label or status text (e.g. "120 KB", "Uploading…"). */
  subtitle?: string;
  /** Image preview data/URL. When set, shown in place of the paperclip tile. */
  previewUrl?: string;
  /** Visual state. `error` outlines the chip; `uploading` dims it. */
  status?: "ready" | "uploading" | "error";
  /** When provided, renders a hover-revealed remove button. */
  onRemove?: () => void;
  /** Accessible label for the remove button (its only content is an icon). */
  removeAriaLabel?: string;
  /** Required on the remove Button per the repo's statsig convention. */
  removeStatsigName?: string;
}

/** Chip container styling per visual state, hoisted so it isn't rebuilt per render. */
const statusClasses: Record<NonNullable<FileChipProps["status"]>, string | undefined> = {
  ready: undefined,
  uploading: "opacity-70",
  error: "border-destructive",
};

/**
 * Presentational chip for an attached file — the shared look used by both the
 * Omnibox attachment tray and the email-draft attachments block. Stateless: the
 * caller owns upload/remove behavior and passes display state in.
 */
export const FileChip = ({
  name,
  subtitle,
  previewUrl,
  status = "ready",
  onRemove,
  removeAriaLabel = "Remove attached file",
  removeStatsigName = "file-chip-remove",
}: FileChipProps) => {
  return (
    <div
      className={cn(
        "group relative flex items-center gap-2 rounded-lg border bg-muted/50 p-2",
        statusClasses[status],
      )}
    >
      {previewUrl ? (
        <img src={previewUrl} alt={name} className="h-12 w-12 rounded object-cover" />
      ) : (
        <div className="flex h-12 w-12 items-center justify-center rounded bg-muted text-muted-foreground">
          <Paperclip className="size-4" />
        </div>
      )}
      <div className="min-w-0 flex-1">
        <p className="max-w-[12rem] truncate text-xs font-medium">{name}</p>
        {subtitle ? <p className="text-xs text-muted-foreground">{subtitle}</p> : null}
      </div>
      {onRemove ? (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onRemove}
          aria-label={removeAriaLabel}
          className="rounded p-1 opacity-0 transition-opacity hover:bg-muted group-hover:opacity-100"
          data-statsig-name={removeStatsigName}
        >
          <X className="size-3" />
        </Button>
      ) : null}
    </div>
  );
};
